/**
 * F1.6.0 questions, section by section. CR-2026-CONTRACT-REVIEW (2026-10-15).
 * Each answer shows how it was established and where (the source page opens beside it). In a DRAFT revision a
 * preparer (edit_contract_review) confirms proposals, answers with a basis, raises RFIs, marks N/A or sends a question
 * to a signatory. Capture proposes; people decide.
 */
import React, { useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Radio, RadioGroup, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import { ExpandMoreRounded } from "@mui/icons-material";
import ApiCall from "../../utils/apicall";
import {
  STATE_COLOR, STATE_LABEL, SECTION_STATUS, answerActions, answerBody, answerProblem, answerText, citationText, confirmNeedsDialog, optLabel,
  sectionConfirmable,
} from "./contractReviewModel";

const msgOf = (e, f) => e?.response?.data?.message || e?.message || f;
const ACTION_LABEL = { confirm: "Confirm", resolve: "Choose", answer: "Answer", na: "N/A", decision: "To signatory", rfi: "RFI", reopen: "Reopen" };

export async function openSource(sourceId, page) {
  const r = await ApiCall.get(`/contract-review/sources/${sourceId}/file`, { responseType: "blob", timeout: 120000 });
  const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
  window.open(page ? `${url}#page=${page}` : url, "_blank", "noopener");
}

function ActionDialog({ open, action, answer, slots, reviewId, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  React.useEffect(() => { setF(action === "resolve" ? { value: answer?.alternatives?.[0] || "" } : {}); setErr(null); }, [action, answer]);
  if (!answer) return null;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    const p = answerProblem(action, answer, f);
    if (p) { setErr(p); return; }
    setBusy(true); setErr(null);
    try {
      const r = action === "rfi"
        ? await ApiCall.post(`/contract-review/reviews/${reviewId}/answers/${answer.key}/rfi`, { requestedOf: f.requestedOf.trim(), question: f.question.trim() })
        : await ApiCall.patch(`/contract-review/reviews/${reviewId}/answers/${answer.key}`, answerBody(action, answer, f));
      onSaved(r.data);
    } catch (e) { setErr(msgOf(e, "Could not save.")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{ACTION_LABEL[action]}: {answer.question}</DialogTitle>
      <DialogContent>
        {answer.hint && <Alert severity="info" sx={{ mb: 1.5 }}>{answer.hint}</Alert>}
        {action === "confirm" && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>Proposed: <b>{answerText({ ...answer, state: "CONFIRMED", value: answer.proposedValue ?? answer.value })}</b>. Read the words before confirming:</Typography>
            {(answer.citations || []).map((c, i) => (
              <Typography key={i} variant="body2" color={c.negated ? "error" : "text.secondary"} sx={{ mb: 0.75 }}>
                {citationText(c)}: "{c.quote}"
              </Typography>
            ))}
          </>
        )}
        {action === "resolve" && (
          <RadioGroup value={f.value || ""} onChange={(e) => set("value", e.target.value)}>
            {(answer.alternatives || []).map((v) => <FormControlLabel key={v} value={v} control={<Radio />} label={answer.type === "text" ? v : optLabel(v)} />)}
          </RadioGroup>
        )}
        {action === "answer" && (answer.type === "yn" ? (
          <RadioGroup row value={f.value || ""} onChange={(e) => set("value", e.target.value)}>
            <FormControlLabel value="Y" control={<Radio />} label="Yes" /><FormControlLabel value="N" control={<Radio />} label="No" />
          </RadioGroup>
        ) : answer.type === "choice" ? (
          <TextField select fullWidth size="small" label="Answer" sx={{ mt: 1 }}
            value={answer.multi ? (f.value || "").split(",").filter(Boolean) : (f.value || "")}
            SelectProps={answer.multi ? { multiple: true } : undefined}
            onChange={(e) => set("value", [].concat(e.target.value).join(","))}>
            {(answer.opts || []).map((o) => <MenuItem key={o} value={o}>{optLabel(o)}</MenuItem>)}
          </TextField>
        ) : (
          <TextField fullWidth size="small" label="Answer" value={f.value || ""} onChange={(e) => set("value", e.target.value)} sx={{ mt: 1 }} inputProps={{ maxLength: 300 }} />
        ))}
        {["answer", "resolve"].includes(action) && (
          <TextField fullWidth size="small" multiline minRows={2} label="Basis (where it is stated, or who decided it)" value={f.basisNote || ""}
            onChange={(e) => set("basisNote", e.target.value)} sx={{ mt: 1.5 }} />
        )}
        {action === "na" && <TextField fullWidth size="small" label="Why it does not apply" value={f.naReason || ""} onChange={(e) => set("naReason", e.target.value)} sx={{ mt: 1 }} />}
        {action === "decision" && (
          <>
            <TextField select fullWidth size="small" label="Signatory who decides" value={f.decisionSlot || ""} onChange={(e) => set("decisionSlot", e.target.value)} sx={{ mt: 1 }}>
              {slots.map((s) => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
            </TextField>
            <TextField fullWidth size="small" multiline minRows={2} label="What must be decided" value={f.decisionNote || ""} onChange={(e) => set("decisionNote", e.target.value)} sx={{ mt: 1.5 }} />
          </>
        )}
        {action === "rfi" && (
          <>
            <TextField fullWidth size="small" label="RFI to (customer, EOR, architect)" value={f.requestedOf || ""} onChange={(e) => set("requestedOf", e.target.value)} sx={{ mt: 1 }} />
            <TextField fullWidth size="small" multiline minRows={3} label="Question" value={f.question || ""} onChange={(e) => set("question", e.target.value)} sx={{ mt: 1.5 }}
              helperText="Ask for the requirement. Do not propose an answer." />
          </>
        )}
        {action === "reopen" && <Typography variant="body2">Puts the question back to its captured proposal (or open) and clears who confirmed it.</Typography>}
        {err && <Alert severity="error" sx={{ mt: 1.5 }}>{err}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy}>{ACTION_LABEL[action]}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function QuestionsPanel({ review, canEdit, onChange, onMessage }) {
  const [dlg, setDlg] = useState(null); // { action, answer }
  const [filter, setFilter] = useState("ALL");
  const status = review.status;
  const confirmQuick = async (a) => {
    try { const r = await ApiCall.patch(`/contract-review/reviews/${review.id}/answers/${a.key}`, answerBody("confirm", a, {})); onChange(r.data.data); }
    catch (e) { onMessage({ severity: "error", text: msgOf(e, "Could not confirm.") }); }
  };
  const confirmSection = async (key) => {
    try { const r = await ApiCall.post(`/contract-review/reviews/${review.id}/sections/${key}/confirm`); onChange(r.data.data); onMessage({ severity: "success", text: r.data.message }); }
    catch (e) { onMessage({ severity: "error", text: msgOf(e, "Could not confirm the section.") }); }
  };
  const shown = (a) => filter === "ALL" || (filter === "OPEN" ? ["PROPOSED", "CONFLICT", "MANUAL"].includes(a.state) || (a.state === "NOT_STATED" && !a.rfi) : a.reviewStatus === filter);
  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
        {[["ALL", "All"], ["OPEN", "Open"], ["NOT_STATED_RFI", "RFI"], ["DECISION_REQUIRED", "Decision"], ["STATED", "Stated"], ["NA", "N/A"]].map(([k, l]) => (
          <Chip key={k} label={l} color={filter === k ? "primary" : "default"} onClick={() => setFilter(k)} sx={{ mb: 0.5 }} />
        ))}
      </Stack>
      {review.sections.map((s) => {
        const list = review.answers.filter((a) => a.section === s.key && shown(a));
        if (!list.length) return null;
        const cited = review.answers.filter((a) => a.section === s.key && sectionConfirmable(a)).length;
        return (
          <Accordion key={s.key} defaultExpanded={s.counts.OPEN > 0} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreRounded />}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
                <Typography sx={{ fontWeight: 600, flex: 1 }}>{s.title}</Typography>
                <Chip size="small" label={SECTION_STATUS[s.status].label} color={SECTION_STATUS[s.status].color} />
                {s.counts.OPEN > 0 && <Chip size="small" variant="outlined" label={`${s.counts.OPEN} open`} />}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {canEdit && status === "DRAFT" && cited > 0 && (
                <Button size="small" variant="outlined" sx={{ mb: 1 }} onClick={() => confirmSection(s.key)}>
                  Confirm {cited} cited proposal{cited === 1 ? "" : "s"} in this section
                </Button>
              )}
              {list.map((a) => {
                const c = (a.citations || [])[0];
                return (
                  <Box key={a.key} sx={{ py: 1, borderTop: 1, borderColor: "divider" }}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "flex-start" }}>
                      <Box sx={{ flex: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.question}{a.sfm ? " *" : ""}</Typography>
                        {a.captureChanged && <Chip size="small" color="warning" label="New information in the documents" sx={{ mt: 0.5 }} />}
                      </Box>
                      <Box sx={{ flex: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip size="small" label={STATE_LABEL[a.state]} color={STATE_COLOR[a.state]} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{answerText(a)}</Typography>
                        </Stack>
                        {a.state === "CONFIRMED" && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {a.origin === "PERSON" ? `Answered by ${a.confirmedByName}: ${a.basisNote || ""}` : `${a.origin === "SFM" ? "From SFM" : a.origin === "CARRIED" ? "Carried from the earlier revision" : "Captured"}, confirmed by ${a.confirmedByName}`}
                          </Typography>
                        )}
                        {c && (
                          <Tooltip title={c.quote || ""}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ cursor: c.sourceId && c.page ? "pointer" : "default", textDecoration: c.sourceId && c.page ? "underline dotted" : "none" }}
                              onClick={() => c.sourceId && c.page && openSource(c.sourceId, c.page).catch((e) => onMessage({ severity: "error", text: msgOf(e, "Could not open the document.") }))}>
                              {citationText(c)}: "{(c.quote || "").slice(0, 140)}{(c.quote || "").length > 140 ? "..." : ""}"
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ flex: 1.4, justifyContent: { md: "flex-end" } }}>
                        {answerActions(a, canEdit, status).map((act) => (
                          <Button key={act} size="small" variant={act === "confirm" ? "contained" : "outlined"} sx={{ mb: 0.5 }}
                            onClick={() => (act === "confirm" && !confirmNeedsDialog(a) ? confirmQuick(a) : setDlg({ action: act, answer: a }))}>{ACTION_LABEL[act]}</Button>
                        ))}
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>* SFM addition (not printed on F1.6.0 Rev 0).</Typography>
      <ActionDialog open={!!dlg} action={dlg?.action} answer={dlg?.answer} slots={review.slots.filter((s) => !s.unused)} reviewId={review.id}
        onClose={() => setDlg(null)} onSaved={(d) => { setDlg(null); onChange(d.data); onMessage({ severity: "success", text: d.message }); }} />
    </Box>
  );
}
